import { type Payee, payeeNameSchema } from '@familywallet/shared';
import { useMutation } from '@tanstack/react-query';
import { ChevronRight } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { FormError } from '../components/FormError';
import { Row } from '../components/FormRow';
import { PageHeader } from '../components/PageHeader';
import { PickerSheet } from '../components/PickerSheet';
import { Button, buttonVariants } from '../components/ui/button';
import { Card, IconSquare, ListGroup, listRowClass } from '../components/ui/card';
import { Field } from '../components/ui/field';
import { Input } from '../components/ui/input';
import { api } from '../lib/api';
import { useCan } from '../lib/auth';
import { usePayeeList } from '../lib/ledger';
import { useInvalidateLedger } from '../lib/ledger-mutations';
import { cn } from '../lib/utils';
import { CategoryChip, CategoryPicker } from './AddPage';

/**
 * Payees (shops and people money goes to or comes from): search, see a payee's transactions,
 * and for those with payees.manage rename, set the category to pre-fill, merge duplicates and
 * archive.
 */
export function PayeesPage() {
  const { t } = useTranslation();
  const [search, setSearch] = useState('');
  const [openId, setOpenId] = useState<string | null>(null);
  const payees = usePayeeList(search.trim());
  const items = payees.data?.items ?? [];
  const open = items.find((p) => p.id === openId);

  return (
    <div className="flex flex-col gap-4">
      <PageHeader title={t('payees.title')} back="/family" />
      <p className="px-1 text-sm text-muted">{t('payees.hint')}</p>
      <Field label={t('payees.search')}>
        <Input type="search" value={search} onChange={(e) => setSearch(e.target.value)} />
      </Field>
      {payees.isError && <FormError error={payees.error} />}
      {open && (
        <PayeeEditor key={open.id} payee={open} all={items} onDone={() => setOpenId(null)} />
      )}
      {payees.isPending ? (
        <p role="status">{t('app.loading')}</p>
      ) : items.length === 0 ? (
        <p className="px-1 text-muted">{t('payees.empty')}</p>
      ) : (
        <ListGroup>
          {items.map((p) => (
            <li key={p.id}>
              <button
                type="button"
                className={cn(listRowClass, 'w-full text-start')}
                aria-expanded={p.id === openId}
                onClick={() => setOpenId(p.id === openId ? null : p.id)}
              >
                <IconSquare>{p.name.slice(0, 1).toUpperCase()}</IconSquare>
                <span className="flex min-w-0 flex-1 flex-col">
                  <span
                    className={cn(
                      'truncate text-[15px] font-medium',
                      p.archived && 'text-muted line-through',
                    )}
                  >
                    {p.name}
                  </span>
                  <span className="truncate text-xs text-muted">
                    {p.archived ? t('payees.archived') : t('payees.uses', { count: p.usageCount })}
                  </span>
                </span>
                <ChevronRight
                  aria-hidden="true"
                  className="size-5 shrink-0 text-muted rtl:rotate-180"
                />
              </button>
            </li>
          ))}
        </ListGroup>
      )}
    </div>
  );
}

function PayeeEditor({ payee, all, onDone }: { payee: Payee; all: Payee[]; onDone: () => void }) {
  const { t } = useTranslation();
  const manage = useCan('payees.manage');
  const invalidate = useInvalidateLedger();
  const [name, setName] = useState(payee.name);
  const [picker, setPicker] = useState<'category' | 'merge' | null>(null);
  const [mergeInto, setMergeInto] = useState<Payee | null>(null);
  const nameValid = payeeNameSchema.safeParse(name).success;

  const done = async () => {
    await invalidate();
  };
  const update = useMutation({
    mutationFn: (body: { name?: string; defaultCategoryId?: string | null }) =>
      api.payees.update(payee.id, body),
    onSuccess: done,
  });
  const archive = useMutation({
    mutationFn: () =>
      payee.archived ? api.payees.unarchive(payee.id) : api.payees.archive(payee.id),
    onSuccess: done,
  });
  const merge = useMutation({
    mutationFn: (intoId: string) => api.payees.merge(payee.id, { intoId }),
    onSuccess: async () => {
      setMergeInto(null);
      await done();
      onDone();
    },
  });

  return (
    <Card className="flex flex-col gap-3">
      <h2 className="text-[15px] font-semibold">{payee.name}</h2>
      <Link
        to={`/activity?payeeId=${payee.id}`}
        className={buttonVariants({ variant: 'secondary' })}
      >
        {t('payees.seeTransactions')}
      </Link>
      {manage && (
        <>
          <form
            noValidate
            className="flex items-end gap-2"
            onSubmit={(e) => {
              e.preventDefault();
              if (nameValid && name.trim() !== payee.name) update.mutate({ name: name.trim() });
            }}
          >
            <div className="flex-1">
              <Field label={t('payees.name')}>
                <Input autoComplete="off" value={name} onChange={(e) => setName(e.target.value)} />
              </Field>
            </div>
            <Button
              type="submit"
              variant="secondary"
              className="w-auto px-4"
              disabled={!nameValid || name.trim() === payee.name || update.isPending}
            >
              {t('payees.rename')}
            </Button>
          </form>
          <ListGroup>
            <Row label={t('payees.defaultCategory')}>
              <CategoryChip
                kind="expense"
                id={payee.defaultCategoryId ?? ''}
                onClick={() => setPicker('category')}
              />
            </Row>
          </ListGroup>
          {payee.defaultCategoryId && (
            <Button
              variant="ghost"
              disabled={update.isPending}
              onClick={() => update.mutate({ defaultCategoryId: null })}
            >
              {t('payees.clearCategory')}
            </Button>
          )}
          <p className="px-1 text-xs text-muted">{t('payees.defaultCategoryHint')}</p>
          <FormError error={update.error} />
          {!payee.archived && (
            <>
              <Button variant="secondary" onClick={() => setPicker('merge')}>
                {t('payees.merge')}
              </Button>
              <p className="px-1 text-xs text-muted">{t('payees.mergeHint')}</p>
            </>
          )}
          {mergeInto && (
            <Card className="flex flex-col gap-3 border border-line">
              <p className="font-medium">
                {t('payees.confirmMerge', { from: payee.name, into: mergeInto.name })}
              </p>
              <FormError error={merge.error} />
              <Button disabled={merge.isPending} onClick={() => merge.mutate(mergeInto.id)}>
                {merge.isPending ? t('common.saving') : t('payees.confirm')}
              </Button>
              <Button variant="ghost" onClick={() => setMergeInto(null)}>
                {t('payees.cancel')}
              </Button>
            </Card>
          )}
          <FormError error={archive.error} />
          <Button variant="ghost" disabled={archive.isPending} onClick={() => archive.mutate()}>
            {payee.archived ? t('payees.unarchive') : t('payees.archive')}
          </Button>
        </>
      )}
      {picker === 'category' && (
        <CategoryPicker
          kind="expense"
          selected={payee.defaultCategoryId ?? ''}
          onPick={(id) => {
            update.mutate({ defaultCategoryId: id });
            setPicker(null);
          }}
          onClose={() => setPicker(null)}
        />
      )}
      {picker === 'merge' && (
        <PickerSheet
          title={t('payees.pickMerge')}
          options={all
            .filter((p) => p.id !== payee.id && !p.archived)
            .map((p) => ({ value: p.id, label: p.name }))}
          selected=""
          onPick={(id) => {
            const into = all.find((p) => p.id === id);
            if (into) setMergeInto(into);
            setPicker(null);
          }}
          onClose={() => setPicker(null)}
          empty={t('payees.empty')}
        />
      )}
    </Card>
  );
}
